import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';

import { colors } from '../../theme/tokens';

export interface SkeletonPipelineProps {
  /** Number of placeholder cards to render. Default 3. */
  count?: number;
}

/**
 * WU12 — Skeleton placeholder for the promotion pipeline cards.
 *
 * Renders `count` (default 3) blank `WorkflowCard`-shaped placeholders
 * while the list query is pending. Mirrors the real card structure —
 * header row, timeline strip, footer row — so the visual layout doesn't
 * jump when the data arrives.
 */
const SkeletonPipeline: React.FC<SkeletonPipelineProps> = ({ count = 3 }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Paper
          key={i}
          elevation={0}
          data-testid="skeleton-pipeline-card"
          sx={{
            p: 2.5,
            borderRadius: '12px',
            border: `1px solid ${colors.borderLight}`,
            backgroundColor: colors.bgWhite,
          }}
        >
          {/* Header row — id, tenant, status chip */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton variant="text" width={80} height={20} />
              <Skeleton variant="text" width={140} height={16} />
              <Skeleton variant="text" width={70} height={14} />
            </Box>
            <Skeleton variant="rounded" width={90} height={24} />
          </Box>

          {/* Timeline strip — 6 dots + connectors */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
            {Array.from({ length: 6 }).map((__, j) => (
              <React.Fragment key={j}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="circular" width={28} height={28} />
                  <Skeleton variant="text" width={36} height={12} />
                </Box>
                {j < 5 && <Skeleton variant="rectangular" sx={{ flex: 1, height: 2 }} />}
              </React.Fragment>
            ))}
          </Box>

          {/* Footer row — primary CTA + details link */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Skeleton variant="rounded" width={110} height={32} />
            <Skeleton variant="text" width={70} height={20} />
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default SkeletonPipeline;
